import React, { useState, useEffect, useContext } from 'react';
import { IComponent, IComponentForm, IComponentPhone } from '../../component';
import { navigatorTo } from '../../utils/util';
import classNames from 'classnames';
import chroma from 'chroma-js';
import intl from 'react-intl-universal';
import { inject, observer } from 'mobx-react';
import ComponentLink from '../ComponentLink';
import { ThemeContext } from '../../store/ThemeProvider';
import { post } from '../../utils/ajax';

import RenderComponent, {
  RenderComponentWithWrapper
} from '../Gallery/RenderComponent';

export const RenderRequired = (value: boolean) => {
  return !!value ? <span style={{ color: '#d90000' }}>*</span> : null;
};

export const FormFieldType = [
  'form_input',
  'form_phone',
  'form_textarea',
  'form_date',
  'form_time',
  'form_radio',
  'form_checkbox',
  'form_select',
  'form_invite',
  'form_password',
  'form_pay',
  'form_portrait'
];

let timer = 0;
const EXHIBITOR_FORM_SUBMITTED_MESSAGE = 'jfoto:exhibitor-form-submitted';

const escapeEmailHtml = (value: any) => {
  return String(value === null || typeof value === 'undefined' ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const buildExhibitorLeadEmailHtml = (postData: any[]) => {
  const exhibitorFieldLabels: { [key: string]: string } = {
    来源展商: '展商名称',
    来源展商ID: '展商ID',
    来源展位: '展位号'
  };
  const exhibitorFieldOrder = ['来源展商', '来源展商ID', '来源展位'];
  const formatLine = (label: string, value: any) => {
    const displayValue = Array.isArray(value) ? value.join('、') : value;
    return `<div style="margin:0 0 8px;line-height:1.7;word-break:break-all;">${escapeEmailHtml(label)}：${escapeEmailHtml(displayValue)}</div>`;
  };
  const formLines = postData
    .filter(item => exhibitorFieldOrder.indexOf(item.field) === -1)
    .map(item => formatLine(item.field, item.value))
    .join('');
  const exhibitorLines = exhibitorFieldOrder
    .map(field => postData.find(item => item.field === field))
    .filter(Boolean)
    .map(item => formatLine(exhibitorFieldLabels[item.field], item.value))
    .join('');

  return `<div style="font-size:16px;color:#222;font-family:Arial,'Microsoft YaHei',sans-serif;"><div style="margin:0 0 24px;line-height:1.7;">收到一条新的展商咨询</div><div style="height:16px;line-height:16px;">&nbsp;</div>${formLines}<div style="height:16px;line-height:16px;">&nbsp;</div>${exhibitorLines}</div>`;
};

const Form = (props: IComponentForm) => {
  const {
    submit,
    children = [] as IComponent[],
    rootStore,
    afteraction,
    submitBehavior,
    customRequest,
    isPortraitMode
  } = props;
  const [validate, setValidate] = useState(false);
  const [data, setData] = useState({} as any);
  const [portraitCompleted, setPortraitCompleted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 检查是否为肖像权模式
  const hasPortraitComponent = children.some((child: IComponent) => child.type === 'form_portrait');
  const isPortrait = isPortraitMode || (children.length === 1 && hasPortraitComponent);

  const urlParse = new URL(window.location.href);
  const isEmbeddedExhibitorForm =
    urlParse.searchParams.get('embedded_exhibitor_form') === '1';
  const embeddedParentLinkId =
    urlParse.searchParams.get('embedded_parent_linkid') || undefined;
  const exhibitorEmail = urlParse.searchParams.get('email_to') || '';
  const finishEmbeddedSubmission = () => {
    if (isEmbeddedExhibitorForm) setIsSubmitting(false);
  };

  // 肖像权授权完成回调
  const onPortraitComplete = () => {
    if (window.location.href.includes('isPreview')) {
      return;
    }
    setPortraitCompleted(true);
    //  /g/fee2dbe/visit/e0hup2u6 => /g/e0hup2u6
    const visitLinkId = window.location.pathname.split('/')[4];
    ComponentLink.to(`/g/${visitLinkId}`);
  };

  const onFieldChange = (type: string, field: string, value: any) => {
    // 接收数据，并判断是否允许提交了
    let newData = {
      ...data,
      [field]: {
        field,
        type,
        value
      }
    };

    setData(newData);

    let checkResult = checkFieldRequired(newData);

    setValidate(checkResult.checked);
  };

  const checkNeedAuth = () => {
    // 判断是否一开始就需要微信授权
    // 判断一下当前是否含有需要报名的
    // 展商 Drawer 内嵌表单不参与页面级微信授权，避免桌面浏览器无法完成留资。
    if (isEmbeddedExhibitorForm) return true;
    let exist = children.filter((child: any) => {
      if (['form_phone', 'form_invite'].indexOf(child.type) > -1) {
        return true;
      }
      return false;
    });

    if (exist && exist.length) {
      let user = rootStore.wxuserStore!.user;
      if (user && user.is_mock === false) {
        // 授权过的
        return true;
      } else {
        rootStore.wxuserStore!.auth(true);
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    checkNeedAuth();
  }, [children]);

  const [errorMessage, setErrorMessage] = useState('');

  const checkFieldRequired = (newData: any) => {
    let checked = true;
    let errorField = [] as any[];
    children.forEach((it: any) => {
      if (it) {
        let { field, required, type } = it;
        if (FormFieldType.indexOf(type) > -1) {
          if (required) {
            let target = newData[field];
            if (!target || !target.value) {
              checked = false;
              errorField.push(it);
            }
          }
        }
      }
    });

    return {
      checked,
      errorField
    };
  };

  const checkPhoneField = () => {
    let existPhone = children.find((it: any) => {
      if (it && it.type == 'form_phone') {
        return true;
      }
      return false;
    });

    if (existPhone) {
      if ((existPhone as IComponentPhone).whitelist) {
        return 0;
      }
    }

    return 1;
  };

  const checkFormAction = () => {
    let action = 'regist';
    for (var i = 0; i < children.length; i++) {
      let item = children[i];
      if (item.type == 'form_invite') {
        action = 'invite';
        break;
      }
      // if(item.type == 'form_password') {
      //   action = 'password';
      //   break;
      // }
    }

    if (action === 'regist') {
      let phoneField = checkPhoneField();
      if (phoneField === 0) {
        action = 'link';
      }
    }

    return action;
  };

  const jumpAfterSubmit = () => {
    if (isEmbeddedExhibitorForm) {
      window.setTimeout(() => {
        window.parent.postMessage(
          { type: EXHIBITOR_FORM_SUBMITTED_MESSAGE },
          window.location.origin
        );
      }, 900);
      return;
    }
    if (submitBehavior && submitBehavior.type) {
      //  无跳转
      if (submitBehavior.type === 'empty') {
        return;
      }

      //  这儿有两种情况: 1. 页面 (page) 2. 外部链接 (custom)
      const target = navigatorTo(submitBehavior, true, true);
      if (target) {
        ComponentLink.to(target, true);
        return;
      }
    }

    // 如果包含redirect_url参数，则跳转到该参数
    const redirectUrl = urlParse.searchParams.get('redirect_url');
    if (redirectUrl) {
      window.location.replace(decodeURIComponent(redirectUrl));
      return;
    }

    // 默认跳转逻辑：如果有visitParentLinkId则跳转visitParentLinkId，否则跳转当前页面的link_id
    if (window.visitParentLinkId) {
      ComponentLink.to(`/g/${window.visitParentLinkId}`);
      return;
    }

    //  ??
    if (window.pageDetail && window.pageDetail.link_id) {
      ComponentLink.to(`/g/${window.pageDetail.link_id}`);
    }
  };

  const doPostForm = (args: any[]) => {
    const [whitelist, action] = args;
    let postData = children
      .map((it: any) => {
        const { field, type, required } = it;
        if (FormFieldType.indexOf(type) > -1) {
          if (!!data[field] && !!data[field].value) {
            return {
              type,
              field,
              required,
              value: data[field].value
            };
          }
        }
        return null;
      })
      .filter(Boolean);

    // 检测是否包含URL传参数据
    urlParse.searchParams.forEach((value, key) => {
      if (key && key.indexOf(`hidden_`) == 0) {
        const saveKey = key.replace(/^hidden_/, ``);
        postData.push({
          type: `input`,
          field: saveKey,
          required: false,
          value: value
        });
      }
    });

    if (action && postData && postData.length) {
      rootStore
        .pagedataStore!.validatePhoneV2(
          window.pageDetail!.link_id,
          whitelist,
          whitelist ? `regist` : `link`
        )
        .then((phoneRes: any) => {
          let afterpromise = null;
          if (afteraction) {
            afterpromise = new Promise(resolve => {
              window
                .fetch(afteraction, {
                  method: 'POST',
                  headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    raw: postData,
                    unionid: props.rootStore.wxuserStore.user.openid
                  }),
                  credentials: 'omit'
                })
                .then((res: any) => {
                  if (res && res.status == 200 && res.ok) {
                    // props.rootStore.uiStore!.showToast({
                    //   content: intl.get('PostPaperSuccess')
                    // });
                    // ComponentLink.to(`/g/${window.visitParentLinkId}`);
                    resolve(true);
                  } else {
                    res.json().then((error: any) => {
                      props.rootStore.uiStore!.showToast({
                        icon: 'error-no-circle',
                        content: error ? error.error : '提交失败'
                      });
                    });
                    resolve(false);
                  }
                })
                .catch((e: any) => {
                  resolve(false);
                });
            });
          } else {
            afterpromise = new Promise(resolve => resolve(true));
          }

          afterpromise.then((afterres: any) => {
            if (afterres) {
              rootStore
                .pagedataStore!.validatePostPaperV2(
                  window.pageDetail!.link_id,
                  isEmbeddedExhibitorForm
                    ? embeddedParentLinkId
                    : window.visitParentLinkId,
                  action,
                  postData
                )
                .then(async (paperRes: any) => {
                  if (paperRes && paperRes.code == 200) {
                    if (isEmbeddedExhibitorForm && exhibitorEmail) {
                      try {
                        const sourceExhibitor = postData.find(item => item.field === '来源展商');
                        const emailRes: any = await post('/api/v2/common/email/send', {
                          from: '展商留资通知',
                          to: exhibitorEmail,
                          title: `新留资 - ${(sourceExhibitor && sourceExhibitor.value) || '展商'}`,
                          html: buildExhibitorLeadEmailHtml(postData),
                          linkid: window.pageDetail!.link_id,
                          embedded_exhibitor_form: true
                        });
                        if (!emailRes || !emailRes.success) {
                          throw new Error((emailRes && emailRes.message) || '邮件发送失败');
                        }
                      } catch (error) {
                        finishEmbeddedSubmission();
                        props.rootStore.uiStore!.showToast({
                          icon: 'error-no-circle',
                          content: `表单已提交，但邮件通知发送失败：${(error as Error).message || '请稍后重试'}`
                        });
                        return;
                      }
                    }
                    // 嵌入式展商表单由外层页面在 Drawer 关闭后统一提示成功，
                    // 普通表单继续保留原有成功反馈。
                    if (!isEmbeddedExhibitorForm) {
                      props.rootStore.uiStore!.showToast({
                        content: intl.get('PostPaperSuccess')
                      });
                    }
                    jumpAfterSubmit();
                  } else {
                    finishEmbeddedSubmission();
                    const errorCode = paperRes ? paperRes.code : '';
                    const errorKey = 'PostPaperError' + errorCode;
                    const localizedError = intl.get(errorKey);
                    props.rootStore.uiStore!.showToast({
                      icon: 'error-no-circle',
                      content:
                        localizedError && localizedError !== errorKey
                          ? localizedError
                          : (paperRes && (paperRes.message || paperRes.error)) ||
                            `提交失败${errorCode ? `（${errorCode}）` : ''}，请稍后重试`
                    });
                  }
                })
                .catch((error: any) => {
                  if (!isEmbeddedExhibitorForm) throw error;
                  finishEmbeddedSubmission();
                  props.rootStore.uiStore!.showToast({
                    icon: 'error-no-circle',
                    content: '提交失败，请稍后重试'
                  });
                });
            } else {
              finishEmbeddedSubmission();
            }
          });
        })
        .catch((error: any) => {
          if (!isEmbeddedExhibitorForm) throw error;
          finishEmbeddedSubmission();
          props.rootStore.uiStore!.showToast({
            icon: 'error-no-circle',
            content: '提交失败，请稍后重试'
          });
        });
    } else {
      finishEmbeddedSubmission();
      console.error(`提交失败 参数不正确`, action, postData);
    }
  };

  const onSubmit = () => {
    if (isEmbeddedExhibitorForm && isSubmitting) return;
    console.log(submitBehavior);
    console.log(validate, data);
    if (validate) {
      // 这里要去提交验证接口了
      // 先判断当前表单里，是否有手机号组件，先找到再说
      // 然后还要判断当前的核心验证逻辑是谁
      // 最后才去提交表单

      // 最后多加一个情况，是不是需要授权
      // let authSuccess = checkNeedAuth();
      // if(authSuccess) {
      if (customRequest && typeof customRequest == 'function') {
        customRequest(data);
      } else {
        if (isEmbeddedExhibitorForm) setIsSubmitting(true);
        Promise.all([checkPhoneField(), checkFormAction()]).then(doPostForm);
      }
      // }
    } else {
      // 此处应该去检查，是哪个接口没通过

      let checkResult = checkFieldRequired(data);
      const { errorField } = checkResult;

      if (errorField && errorField.length) {
        let error = errorField[0];
        window.clearTimeout(timer);
        if (error.field == '付费观看') {
          setErrorMessage('没有该手机号的支付记录');
        } else {
          setErrorMessage(intl.get(`Please input the right`) + error.field);
        }
        timer = window.setTimeout(() => {
          setErrorMessage('');
        }, 3000);
      }
    }
  };

  const {
    backgroundColor,
    componentColor,
    mainColor,
    h1Color,
    h2Color
  } = useContext(ThemeContext);

  const PortraitRenderComponent = RenderComponentWithWrapper as React.ComponentType<any>;

  return (
    <div className="component-form">
      {/*自定义皮肤部分*/}
      <style>
        {`
        body {
          --weui-BG-0: ${chroma(componentColor)
            .alpha(1)
            .css()};
          --weui-BG-1: ${chroma(componentColor)
            .alpha(0.5)
            .css()};
          --weui-BG-2: ${componentColor};
          --weui-BG-3: ${componentColor};
          --weui-BG-4: ${componentColor};
          --weui-BG-5: ${componentColor};
          --weui-FG-0: ${h1Color};
          --weui-FG-HALF: rgba(0, 0, 0, 0.9);
          --weui-FG-1: ${h2Color};
          --weui-FG-2: ${h2Color};
          --weui-FG-3: ${chroma(h1Color)
            .alpha(0.3)
            .css()};
          --weui-FG-4: ${chroma(h1Color)
            .alpha(0.1)
            .css()};
          --weui-RED: #fa5151;
          --weui-ORANGE: #fa9d3b;
          --weui-YELLOW: #ffc300;
          --weui-GREEN: #91d300;
          --weui-LIGHTGREEN: #95ec69;
          --weui-BRAND: ${mainColor};
          --weui-BLUE: #10aeff;
          --weui-INDIGO: #1485ee;
          --weui-PURPLE: #6467f0;
          --weui-WHITE: #fff;
          --weui-LINK: #576b95;
          --weui-TEXTGREEN: #06ae56;
          --weui-FG: black;
          --weui-BG: white;
          --weui-TAG-TEXT-ORANGE: #fa9d3b;
          --weui-TAG-BACKGROUND-ORANGE: rgba(250, 157, 59, 0.1);
          --weui-TAG-TEXT-GREEN: ${chroma(mainColor)
            .alpha(0.7)
            .css()};
          --weui-TAG-BACKGROUND-GREEN: rgba(6, 174, 86, 0.1);
          --weui-TAG-TEXT-BLUE: #10aeff;
          --weui-TAG-BACKGROUND-BLUE: rgba(16, 174, 255, 0.1);
          --weui-TAG-TEXT-BLACK: rgba(0, 0, 0, 0.5);
          --weui-TAG-BACKGROUND-BLACK: rgba(0, 0, 0, 0.05);
          --weui-BTN-DEFAULT-BG: ${h2Color};
          --weui-BTN-DEFAULT-COLOR: ${mainColor};
          --weui-BG-COLOR-ACTIVE: ${chroma(mainColor)
            .alpha(0.1)
            .css()};
        }

        .weui-picker__mask {
          background-image:linear-gradient(180deg,${chroma(componentColor)
            .alpha(1)
            .css()},${chroma(componentColor)
              .alpha(0)
              .css()}),linear-gradient(0deg,${chroma(componentColor)
                .alpha(1)
                .css()},${chroma(componentColor)
                  .alpha(0)
                  .css()});
        }

        .weui-picker {
          background-color:var(--weui-BG-0);
        }

        .weui-cells__group_form .weui-vcode-btn {
          background-color:var(--weui-BG-2);
          border:1px solid var(--weui-FG-4);
        }

        .weui-btn {
          color:${h1Color};
        }

        .weui-btn_primary {
          color:var(--weui-FG-0);
        }

        .weui-btn_disabled {
          background-color:var(--weui-BG-COLOR-ACTIVE);
        }

        .weui-cells__title_center {
          text-align: center;
        }

        .weui-cells__title {
          font-size:12px;
          color:var(--weui-FG-0);
        }

        .weui-cell__width_67 {
          width:50%;
          margin:0 25%;
        }

        .weui-cell__width_25 {
          width:25%;
          margin: 0 37.5%;
        }

        .weui-cell__required {
          margin-left:5px;
          color:#d90000;
          font-size:12px;
        }

        .weui-cell__bd {
          /*font-size:14px;*/
        }

        .weui-cell__bd p {
          margin:0;
        }
`}
      </style>
      <div className="weui-form">
        {/* 肖像权模式：只显示肖像权组件 */}
        {isPortrait && !portraitCompleted && (
          <div className="weui-form__control-area">
            {children.filter((child: IComponent) => child.type === 'form_portrait').map((child: IComponent, index: number) => {
              return (
                <PortraitRenderComponent
                  key={index}
                  {...child}
                  onComplete={onPortraitComplete}
                />
              );
            })}
          </div>
        )}
        {/* 普通表单模式或肖像权完成后 */}
        {(!isPortrait || portraitCompleted) && (
          <>
            <div className="weui-form__control-area">
              {children.filter((child: IComponent) => child.type !== 'form_portrait').map((child: IComponent, index: number) => {
                return (
                  <RenderComponentWithWrapper
                    key={index}
                    {...child}
                    onChange={onFieldChange}
                    // @ts-ignore
                    formData={data}
                  />
                );
              })}
            </div>
            {children.filter((child: IComponent) => child.type !== 'form_portrait').length > 0 && (
              <div className="weui-form__opr-area">
                <a
                  className={`weui-btn weui-btn_primary ${validate && !(isEmbeddedExhibitorForm && isSubmitting) ? '' : 'weui-btn_disabled'
                    }`}
                  onClick={onSubmit}
                  aria-disabled={!validate || (isEmbeddedExhibitorForm && isSubmitting)}
                >
                  {isEmbeddedExhibitorForm && isSubmitting ? '提交中…' : submit}
                </a>
              </div>
            )}
          </>
        )}
        {errorMessage ? (
          <div
            className="weui-toptips weui-toptips_warn"
            style={{ display: `block`, opacity: 1 }}
          >
            {errorMessage}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default inject('rootStore')(observer(Form));
